import { useMemo } from "react";
import { useGetCountries, useGetCountryCurrency } from "../../../../api/static-data";

export interface CompanySelectOption {
  id: string | number;
  title: string;
  value: string | number;
}

/**
 * Country + currency choices for the company forms. Same sources and labels
 * as the legacy modals (`/static-data/countries` → `c.id`/`c.country`;
 * `/static-data/country-currency` → `c._id`, "country - currency - code").
 * The selects hold string values, so `resolveCountry`/`resolveCurrency` map
 * a chosen value back to the option's *original* id (number for countries,
 * whatever `_id` is for currencies) before it goes into a payload.
 */
export const useCompanyStaticOptions = () => {
  const { data: countriesData, isLoading: isCountriesLoading } = useGetCountries();
  const { data: currencyData, isLoading: isCurrencyLoading } = useGetCountryCurrency();

  const countries = useMemo<CompanySelectOption[]>(
    () =>
      ((countriesData as unknown as { id: number; country: string }[] | undefined) || []).map(
        c => ({
          id: c.id,
          title: c.country,
          value: c.id
        })
      ),
    [countriesData]
  );

  const currencies = useMemo<CompanySelectOption[]>(
    () =>
      (currencyData || []).map((c: any) => ({
        id: c._id,
        title: `${c.country} - ${c.currency} - ${c.alphabeticCode}`,
        value: c._id
      })),
    [currencyData]
  );

  const resolve = (options: CompanySelectOption[], value: string | undefined) => {
    if (value === undefined || value === null || value === "") return undefined;
    return options.find(o => String(o.value) === String(value))?.value;
  };

  return {
    countries,
    currencies,
    isCountriesLoading,
    isCurrencyLoading,
    resolveCountry: (value?: string) => resolve(countries, value),
    resolveCurrency: (value?: string) => resolve(currencies, value)
  };
};
